import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Save as SaveIcon
} from '@material-ui/icons'
import { toast } from 'react-toastify'
import axios from 'axios'
import moment from 'moment'

const LeadEditModal = ({ open, lead, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    notes: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    status: 'new',
    followUpDate: '',
    followUpNotes: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (lead) {
      setFormData({
        companyName: lead.companyName || '',
        address: lead.address || '',
        notes: lead.notes || '',
        contactName: lead.contactName || '',
        contactEmail: lead.contactEmail || '',
        contactPhone: lead.contactPhone || '',
        status: lead.status || 'new',
        followUpDate: lead.followUpDate ? moment(lead.followUpDate).format('YYYY-MM-DD') : '',
        followUpNotes: lead.followUpNotes || ''
      })
      setErrors({})
    }
  }, [lead])

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    })
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required'
    }
    
    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format'
    }
    
    if (formData.contactPhone && !/^[\d\s\-\(\)\+]+$/.test(formData.contactPhone)) {
      newErrors.contactPhone = 'Invalid phone format'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const updateData = {
        ...formData,
        followUpDate: formData.followUpDate || null
      }

      const response = await axios.put(`/api/company/leads/${lead._id}`, updateData)
      
      if (response.data.success) {
        toast.success('Lead updated successfully')
        onSave(response.data.data)
      }
    } catch (error) {
      console.error('Error updating lead:', error)
      toast.error(error.response?.data?.error || 'Failed to update lead')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Edit Lead</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Company Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Company Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Name *"
              value={formData.companyName}
              onChange={handleChange('companyName')}
              error={Boolean(errors.companyName)}
              helperText={errors.companyName}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={handleChange('status')}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="qualified">Qualified</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address *"
              value={formData.address}
              onChange={handleChange('address')}
              error={Boolean(errors.address)}
              helperText={errors.address}
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Contact Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Name"
              value={formData.contactName}
              onChange={handleChange('contactName')}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.contactEmail}
              onChange={handleChange('contactEmail')}
              error={Boolean(errors.contactEmail)}
              helperText={errors.contactEmail}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.contactPhone}
              onChange={handleChange('contactPhone')}
              error={Boolean(errors.contactPhone)}
              helperText={errors.contactPhone}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Notes
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={4}
              value={formData.notes}
              onChange={handleChange('notes')}
            />
          </Grid>

          {/* Follow-up Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Follow-up Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Follow-up Date"
              type="date"
              value={formData.followUpDate}
              onChange={handleChange('followUpDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Follow-up Notes"
              multiline
              rows={3}
              value={formData.followUpNotes}
              onChange={handleChange('followUpNotes')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LeadEditModal